import { Component, computed, EventEmitter, forwardRef, inject, Input, Output, signal, ViewChild } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import {
  IonicSelectableComponent,
  IonicSelectableHeaderTemplateDirective,
  IonicSelectableItemTemplateDirective,
  IonicSelectableValueTemplateDirective,
} from 'ionic-selectable'
import { IonicModule } from '@ionic/angular'
import { ClientService } from '../../../../services/domain/client/client.service'
import { Client } from '../../../../models/client'
import { searchFilter } from '../../../../services/utils'

@Component({
  selector: 'app-client-select',
  templateUrl: './client-select.component.html',
  styleUrls: ['./client-select.component.scss'],
  standalone: true,
  imports: [
    IonicSelectableComponent,
    IonicModule,
    FormsModule,
    IonicSelectableComponent,
    IonicSelectableValueTemplateDirective,
    IonicSelectableHeaderTemplateDirective,
    IonicSelectableItemTemplateDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClientSelectComponent),
      multi: true,
    },
  ],
})
export class ClientSelectComponent implements ControlValueAccessor {
  private clientsService = inject(ClientService)

  @ViewChild('selectableComponent')
  selectableComponent: IonicSelectableComponent

  /** Emits the chosen client id whenever a client is picked (in addition to the CVA form value). */
  @Output() clientSelected = new EventEmitter<number>()

  public disabled: boolean
  private _onChange: (value: number) => void
  protected _onTouched: () => void

  private clients = toSignal(this.clientsService.getActiveWithCaching(), { initialValue: [] as Client[] })
  private value = signal<number | null>(null)
  protected searchPhrase = signal('')
  private excludedIds = signal<number[]>([])

  /** Client ids to hide from the picker, e.g. clients already added elsewhere. */
  @Input() set excludedClientIds(ids: number[] | null) {
    this.excludedIds.set(ids ?? [])
  }

  protected selectedItem = computed(() => this.clients().find((c) => c.id === this.value()) ?? null)
  protected filteredClients = computed(() => {
    const available = this.clients().filter((c) => !this.excludedIds().includes(c.id))
    return searchFilter(available, this.searchPhrase(), ['user.fullname'])
  })

  registerOnChange(fn: (value: number) => void): void {
    this._onChange = fn
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }
  writeValue(value: number): void {
    this.value.set(value ?? null)
  }

  searchClients(text: string) {
    this.searchPhrase.set(text)
  }

  /** Opens the client picker modal programmatically (e.g. from an external button). */
  async open() {
    await this.selectableComponent?.open()
  }

  async closeClientSelect() {
    await this.selectableComponent.close()
  }

  /**
   * Clears the current selection without notifying the CVA/form. For one-shot "pick a client to do X with"
   * usages (not a persistent form field) — otherwise picking the same client again after a blocked attempt is
   * a no-op, since ionic-selectable's (onChange) only fires on an actual value change.
   */
  reset(): void {
    this.value.set(null)
  }

  itemSelected(event: { component: IonicSelectableComponent; value: Client }) {
    this.value.set(event.value.id)
    // Guarded: when used outside a form (no CVA registration) there is no _onChange.
    this._onChange?.(event.value.id)
    this.clientSelected.emit(event.value.id)
  }
}
